import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, Search, RefreshCw, Download, FileBox, Mail, MessageCircle,
  Building2, Package, Calendar, ShieldCheck, Inbox,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, api, getToken, setToken, clearToken, formatApiErrorDetail } from "@/lib/api";
import { PortfolioManager } from "@/components/site/PortfolioManager";

const STATUSES = ["Novo", "Em análise", "Proposta enviada", "Em produção", "Concluído", "Cancelado"];
const STATUS_COLOR = {
  "Novo": "text-[#38BDF8] bg-[#38BDF8]/10 border-[#38BDF8]/30",
  "Em análise": "text-[#FBBF24] bg-[#FBBF24]/10 border-[#FBBF24]/30",
  "Proposta enviada": "text-[#A855F7] bg-[#A855F7]/10 border-[#A855F7]/30",
  "Em produção": "text-[#F97316] bg-[#F97316]/10 border-[#F97316]/30",
  "Concluído": "text-[#10B981] bg-[#10B981]/10 border-[#10B981]/30",
  "Cancelado": "text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/30",
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [checking, setChecking] = useState(!!getToken());

  useEffect(() => {
    if (getToken()) {
      api.get("/auth/me").then(() => { setAuthed(true); setChecking(false); })
        .catch(() => { clearToken(); setAuthed(false); setChecking(false); });
    }
  }, []);

  if (checking) return <div className="min-h-screen bg-ax-bg flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-ax-orange" /></div>;
  return authed ? <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} /> : <Login onLogin={() => setAuthed(true)} />;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success("Sessão iniciada");
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ax-bg flex items-center justify-center px-5 ax-grid">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md bg-ax-card border border-ax-border rounded-2xl p-8 shadow-2xl">
        <a href="/" className="font-heading font-extrabold text-xl text-ax-text">Axio<span className="text-ax-orange">Form</span> 3D</a>
        <h1 className="font-heading text-2xl font-bold text-ax-text mt-6">Área de Gestão</h1>
        <p className="text-sm text-ax-sub mt-1">Aceda ao painel de pedidos.</p>
        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-sm text-ax-sub mb-1.5 block">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="ax-input" placeholder="admin@axioform3d.com" />
          </div>
          <div>
            <Label className="text-sm text-ax-sub mb-1.5 block">Palavra-passe</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="ax-input" placeholder="••••••••" />
          </div>
        </div>
        <button data-testid="admin-login-submit" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-ax-orange hover:bg-ax-orangehover disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-all">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("Todos");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("pedidos");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get("/admin/quotes", { params: { status: filter, q: search || undefined } }),
        api.get("/admin/quotes/stats"),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch {
      toast.error("Falha ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const updateQuote = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, patch);
      setQuotes((prev) => prev.map((x) => (x.id === id ? data : x)));
      if (selected?.id === id) setSelected(data);
      api.get("/admin/quotes/stats").then((r) => setStats(r.data));
      toast.success("Pedido atualizado");
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  const download = async (quoteId, file) => {
    try {
      const res = await api.get(`/admin/quotes/${quoteId}/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = file.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao descarregar ficheiro");
    }
  };

  return (
    <div className="min-h-screen bg-ax-bg" data-testid="admin-order-panel">
      <header className="sticky top-0 z-40 bg-ax-bg/90 backdrop-blur-xl border-b border-ax-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="font-heading font-extrabold text-lg text-ax-text">Axio<span className="text-ax-orange">Form</span> 3D <span className="text-ax-muted font-normal text-sm">/ Gestão</span></a>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="text-ax-sub hover:text-ax-text p-2"><RefreshCw className="w-4 h-4" /></button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-ax-sub hover:text-red-400"><LogOut className="w-4 h-4" /> Sair</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-1 mb-8 border-b border-ax-border">
          <TabButton active={tab === "pedidos"} onClick={() => setTab("pedidos")} testid="tab-pedidos">Pedidos</TabButton>
          <TabButton active={tab === "portfolio"} onClick={() => setTab("portfolio")} testid="tab-portfolio">Portfólio</TabButton>
        </div>
        {tab === "pedidos" && (<>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-8">
          <StatCard label="Total" value={stats.Total || 0} active={filter === "Todos"} onClick={() => setFilter("Todos")} testid="stat-total" />
          {STATUSES.map((s) => (
            <StatCard key={s} label={s} value={stats[s] || 0} active={filter === s} onClick={() => setFilter(s)} testid={`stat-${s}`} color={STATUS_COLOR[s]} />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ax-muted" />
            <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar por nome, empresa, email, referência..." className="ax-input pl-9" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-filter" className="ax-input sm:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Todos">Todos os estados</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-ax-orange" /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-20 text-ax-muted"><Inbox className="w-10 h-10 mx-auto mb-3" />Sem pedidos para mostrar.</div>
        ) : (
          <div className="space-y-3" data-testid="admin-quote-list">
            {quotes.map((q) => (
              <QuoteRow key={q.id} q={q} onOpen={() => setSelected(q)} onStatus={(st) => updateQuote(q.id, { status: st })} />
            ))}
          </div>
        )}
        </>)}
        {tab === "portfolio" && <PortfolioManager />}
      </main>

      {selected && (
        <QuoteDrawer
          q={selected}
          onClose={() => setSelected(null)}
          onStatus={(st) => updateQuote(selected.id, { status: st })}
          onNotes={(notes) => updateQuote(selected.id, { internal_notes: notes })}
          onDownload={(f) => download(selected.id, f)}
        />
      )}
    </div>
  );
}

const TabButton = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-ax-orange text-ax-text" : "border-transparent text-ax-muted hover:text-ax-sub"}`}>
    {children}
  </button>
);

const StatCard = ({ label, value, active, onClick, testid, color }) => (
  <button data-testid={testid} onClick={onClick}
    className={`text-left rounded-xl border p-3.5 transition-all ${active ? "border-ax-orange bg-ax-card" : "border-ax-border bg-ax-card/50 hover:border-ax-border"}`}>
    <div className="font-heading text-2xl font-bold text-ax-text">{value}</div>
    <div className={`text-[11px] font-mono uppercase tracking-wider mt-0.5 truncate ${color ? color.split(" ")[0] : "text-ax-muted"}`}>{label}</div>
  </button>
);

const StatusBadge = ({ status }) => (
  <span className={`text-xs font-mono px-2.5 py-1 rounded border ${STATUS_COLOR[status] || ""}`}>{status}</span>
);

function QuoteRow({ q, onOpen, onStatus }) {
  return (
    <div data-testid={`quote-row-${q.ref_code}`} className="bg-ax-card border border-ax-border rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-ax-cyan/40 transition-colors">
      <button onClick={onOpen} className="flex-1 text-left min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-ax-orange font-bold">{q.ref_code}</span>
          <StatusBadge status={q.status} />
          {q.nda_requested && <span className="text-xs text-ax-cyan flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> NDA</span>}
        </div>
        <div className="mt-1.5 font-heading font-semibold text-ax-text truncate">{q.name}{q.company ? ` · ${q.company}` : ""}</div>
        <div className="text-sm text-ax-sub truncate mt-0.5">{q.description}</div>
        <div className="text-xs text-ax-muted mt-1.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {q.files?.length || 0} ficheiro(s)</span>
          <span>{new Date(q.created_at).toLocaleString("pt-PT")}</span>
        </div>
      </button>
      <div className="sm:w-52 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Select value={q.status} onValueChange={onStatus}>
          <SelectTrigger data-testid={`row-status-${q.ref_code}`} className="ax-input"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
}

function QuoteDrawer({ q, onClose, onStatus, onNotes, onDownload }) {
  const [notes, setNotes] = useState(q.internal_notes || "");
  useEffect(() => { setNotes(q.internal_notes || ""); }, [q.id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-quote-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-ax-surface border-l border-ax-border h-full overflow-y-auto p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="font-mono text-ax-orange font-bold text-lg">{q.ref_code}</span>
            <StatusBadge status={q.status} />
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="text-ax-muted hover:text-ax-text text-2xl leading-none">×</button>
        </div>

        <h3 className="font-heading text-xl font-bold text-ax-text">{q.name}</h3>
        <div className="mt-4 space-y-2.5 text-sm">
          <Row icon={Building2} label="Empresa" value={q.company || "—"} />
          <Row icon={Mail} label="Email" value={<a href={`mailto:${q.email}`} className="text-ax-cyan hover:underline">{q.email}</a>} />
          <Row icon={MessageCircle} label="WhatsApp" value={q.whatsapp || "—"} />
          <Row icon={Package} label="Projeto" value={`${q.project_type || "—"} · qtd ${q.quantity}`} />
          <Row icon={Calendar} label="Prazo" value={q.deadline || "—"} />
          <Row icon={FileBox} label="Tecnologia / Material" value={`${q.technology || "—"} · ${q.material || "—"}`} />
        </div>

        <div className="mt-5 bg-ax-card border border-ax-border rounded-lg p-4">
          <div className="text-xs font-mono uppercase tracking-wider text-ax-muted mb-2">Descrição</div>
          <p className="text-sm text-ax-sub whitespace-pre-wrap leading-relaxed">{q.description}</p>
        </div>

        <div className="mt-5">
          <div className="text-xs font-mono uppercase tracking-wider text-ax-muted mb-2">Ficheiros ({q.files?.length || 0})</div>
          {q.files?.length ? q.files.map((f) => (
            <button key={f.id} data-testid={`drawer-file-${f.id}`} onClick={() => onDownload(f)} className="w-full flex items-center justify-between bg-ax-card border border-ax-border rounded-lg px-4 py-2.5 mb-2 hover:border-ax-cyan/50 transition-colors">
              <span className="flex items-center gap-2 min-w-0"><FileBox className="w-4 h-4 text-ax-orange shrink-0" /><span className="truncate text-sm text-ax-text">{f.original_filename}</span></span>
              <Download className="w-4 h-4 text-ax-cyan shrink-0" />
            </button>
          )) : <p className="text-sm text-ax-muted">Sem ficheiros anexados.</p>}
        </div>

        <div className="mt-6">
          <Label className="text-sm text-ax-sub mb-1.5 block">Estado</Label>
          <Select value={q.status} onValueChange={onStatus}>
            <SelectTrigger data-testid="drawer-status" className="ax-input"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="mt-5">
          <Label className="text-sm text-ax-sub mb-1.5 block">Observações internas</Label>
          <Textarea data-testid="drawer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="ax-input resize-none" placeholder="Notas privadas sobre este pedido..." />
          <button data-testid="drawer-save-notes" onClick={() => onNotes(notes)} className="mt-2 bg-ax-cyan hover:bg-ax-cyanhover text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">Guardar observações</button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-ax-muted mt-0.5 shrink-0" />
    <div className="min-w-0"><span className="text-ax-muted">{label}: </span><span className="text-ax-text break-words">{value}</span></div>
  </div>
);
